import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Box } from '@mui/material';
import { InfoCircle } from '@assets/svgs';
import { CollapsedField } from '@components/quoteDetails/CollapsedField';
import { QFCTypeExecuted } from '@components/quoteDetails/QuoteFieldControl';
import { TextField } from '@components/textField/TextField';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';

export interface TextAreaFieldProps extends QFCTypeExecuted {
  onBlur: () => void;
  isRequired: boolean;
  isRequiredBeforeBind?: boolean;
}

/** Multi-line plain text; line breaks are kept as typed. */
export const TextAreaField = ({
  setValue,
  attributeType,
  errorMessage,
  value,
  isCollapsed,
  hoverText,
  externalIcon,
  isRequired,
  isRequiredBeforeBind,
  isReadOnly,
  onBlur,
  additionalInfoLink,
}: TextAreaFieldProps) => {
  const [stateValue, setStateValue] = useState<string | null>(value?.toString() ?? null);

  const debouncedSetValue = useDebouncedCallback((newValue: string | null) => {
    setValue(newValue);
  }, DEBOUNCE_DELAYS.TYPING);

  // Take the saved value when it changes from outside (e.g. after save or reset).
  const [syncedValue, setSyncedValue] = useState(value);
  if (value !== syncedValue) {
    setSyncedValue(value);
    setStateValue(value ?? null);
  }

  if (isCollapsed) {
    return (
      <CollapsedField
        label={attributeType.label}
        value={stateValue ?? undefined}
        deprecated={attributeType.deprecated}
        preserveLineBreaks
      />
    );
  }

  return (
    <Box sx={styles.container}>
      <TextField
        multiline
        minRows={6}
        additionalInfoLink={additionalInfoLink}
        error={!!errorMessage}
        deprecated={attributeType.deprecated}
        helperText={errorMessage ?? attributeType.helpText ?? ' '}
        onBlur={() => {
          // Publish the last edit before Save reads the stored value.
          debouncedSetValue.flush();
          onBlur();
        }}
        inputLabel={attributeType.label}
        inputLabelIcon={hoverText ? <InfoCircle style={{ flexShrink: 0 }} /> : null}
        tooltipTitle={hoverText}
        externalIcon={externalIcon}
        style={{ width: '100%' }}
        value={stateValue ?? ''}
        onChange={(e) => {
          const newValue = e.target.value === '' ? null : e.target.value;
          setStateValue(newValue);
          debouncedSetValue(newValue);
        }}
        required={isRequired}
        requiredBeforeBind={isRequiredBeforeBind}
        disabled={isReadOnly}
      />
    </Box>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& .MuiFormHelperText-root': { margin: 0 },
  },
};
